import type { DmLastResolution } from '../../../services/dmNotifications';
import type { AssignablePatient } from '../notifications/NotificationAssigneeCell';
import {
    MonitoringPatientIdentity,
    type MonitoringPatientSummary
} from '../shared/MonitoringPatientIdentity';
import { MarkAllReviewedButton } from './MarkAllReviewedButton';
import { FROM_MESSAGE_LIST_STATE, messageViewPath } from './messageListParams';
import { DmMessagePanelFields } from './DmMessagePanelFields';
import type { TriageControlsValue } from './triage/TriageControls';

interface DmMessagePanelHeaderProps {
    patientId: string;
    patient: (MonitoringPatientSummary & AssignablePatient) | null;
    patientError: boolean;
    unreviewedCount: number | null;
    lastResolution: DmLastResolution | null;
    triage: TriageControlsValue | null;
    // The list's own params, so the view's neighbours and Back follow the same filters.
    listSearch: string;
    onReviewed: (patientId: string) => Promise<void>;
}

function IdentityPlaceholder({ error }: { error: boolean }) {
    if (error) return <p className="text-sm text-red-500">Couldn't load patient details</p>;
    return (
        <div className="flex items-center gap-3" aria-hidden="true">
            <div className="h-10 w-10 rounded-full bg-gray-200 animate-pulse" />
            <div className="flex flex-col gap-1.5">
                <div className="h-3 w-32 rounded bg-gray-200 animate-pulse" />
                <div className="h-3 w-24 rounded bg-gray-200 animate-pulse" />
            </div>
        </div>
    );
}

export function DmMessagePanelHeader({
    patientId,
    patient,
    patientError,
    unreviewedCount,
    lastResolution,
    triage,
    listSearch,
    onReviewed
}: DmMessagePanelHeaderProps) {
    const viewLink = { to: messageViewPath(patientId, listSearch), state: FROM_MESSAGE_LIST_STATE };
    return (
        <header className="flex flex-shrink-0 flex-col gap-3 border-b border-gray-200 bg-white px-6 py-4 font-inter">
            <div className="flex items-center justify-between gap-3">
                {patient ? (
                    <MonitoringPatientIdentity patient={patient} size="header" link={viewLink} />
                ) : (
                    <IdentityPlaceholder error={patientError} />
                )}
                <div className="flex-shrink-0">
                    <MarkAllReviewedButton
                        patientId={patientId}
                        unreviewedCount={unreviewedCount}
                        lastResolution={lastResolution}
                        onReviewed={onReviewed}
                        variant="primary"
                    />
                </div>
            </div>
            {patient && triage && (
                <>
                    <hr className="mx-1 border-gray-100" />
                    <DmMessagePanelFields patient={patient} triage={triage} />
                </>
            )}
        </header>
    );
}
